import React, { useEffect, useState } from 'react';
import { BusinessCenterOutlined, Check } from '@mui/icons-material';
import { Colors } from 'library';
import { ClickableSurface, EmptyText, Icon, Label, Row, Select, Stack, Text } from 'library/ui';
import { client, consoleLogInDev } from 'shared';
import { useUserContext } from 'UserContext';
import { formatDate, to12HourTime } from 'shared/Dates';

const ISO_WEEK_DAY_ABBREVIATIONS: { [key: number]: string } = {
    1: 'M',
    2: 'Tu',
    3: 'W',
    4: 'Th',
    5: 'F',
    6: 'Sa',
    7: 'Su',
};

interface OpenJobListing {
    id: number;
    headline: string;
    business_job_type: string | null;
    start_date: string;
    end_date: string;
    pay: number | null;
    rate_min: number | null;
    rate_max: number | null;
    daily_schedules: { id: number; start_time: string; end_time: string; day: number; slots: number }[];
    requested_workers: string[];
}

interface OpenJobPickerProps {
    address: number | null;
    onAddressChange: (addressId: number) => void;
    selectedJobId?: number;
    onSelectJob: (jobId?: number) => void;
    emptyText: string;
}

function jobSubtitle(job: OpenJobListing) {
    const parts = [];
    if (job.start_date) {
        parts.push(
            job.end_date && job.end_date !== job.start_date
                ? `${formatDate(job.start_date)} - ${formatDate(job.end_date)}`
                : formatDate(job.start_date),
        );
    }
    if (job.daily_schedules.length > 0) {
        parts.push(job.daily_schedules.map((schedule) => ISO_WEEK_DAY_ABBREVIATIONS[schedule.day]).join(', '));
        parts.push(
            `${to12HourTime(job.daily_schedules[0].start_time)} - ${to12HourTime(job.daily_schedules[0].end_time)}`,
        );
    }
    parts.push(job.pay ? `$${job.pay}/hr` : `$${job.rate_min}-$${job.rate_max}/hr`);
    return parts.join(' · ');
}

export function OpenJobPicker({ address, onAddressChange, selectedJobId, onSelectJob, emptyText }: OpenJobPickerProps) {
    const { user } = useUserContext();
    const [jobs, setJobs] = useState<OpenJobListing[]>([]);

    useEffect(() => {
        if (!address) return;
        client(`api/job-template/?address=${address}&open_jobs=true`).then(setJobs).catch(consoleLogInDev);
    }, [address]);

    const postableAddresses = (user?.address ?? []).filter((entry) => entry.can_post_jobs);

    return (
        <Stack gap="md">
            {postableAddresses.length > 0 && (
                <Row gap="sm">
                    <Label>Location:</Label>
                    <Select
                        value={address ? String(address) : ''}
                        options={postableAddresses.map((entry) => ({
                            key: entry.id.toString(),
                            value: entry.description || '',
                        }))}
                        onChange={(value) => onAddressChange(parseInt(value, 10))}
                    />
                </Row>
            )}
            {jobs.length > 0 ? (
                <Stack gap="none">
                    {jobs.map((job) => (
                        <ClickableSurface
                            key={job.id}
                            selected={selectedJobId === job.id}
                            onClick={() => onSelectJob(job.id)}
                        >
                            <Row gap="sm" justify="between" fullWidth>
                                <Row gap="sm">
                                    <Icon icon={BusinessCenterOutlined} size="sm" tone="accent" />
                                    <Stack gap="none">
                                        <Text weight="semibold">{job.headline || 'Untitled job'}</Text>
                                        <Text size="sm" tone="secondary">
                                            {jobSubtitle(job)}
                                        </Text>
                                        {job.requested_workers.length > 0 && (
                                            <Text size="sm" tone="secondary">
                                                Requested: {job.requested_workers.join(', ')}
                                            </Text>
                                        )}
                                    </Stack>
                                </Row>
                                {selectedJobId === job.id && <Check sx={{ fontSize: 18, color: Colors.secondary }} />}
                            </Row>
                        </ClickableSurface>
                    ))}
                </Stack>
            ) : (
                <EmptyText>{emptyText}</EmptyText>
            )}
        </Stack>
    );
}
